/**
 * Конструктор отчётов (админка).
 *
 * Вкладки: «Шаблоны» (каталог + редактор + жизненный цикл draft→published→archived)
 * и «Отчёты» (экземпляры: создание, генерация, скачивание).
 *
 * Meta-справочники (статусы-это lifecycle, а типы/источники/движки) приходят
 * только из GET /api/v1/reports/metadata/qualifiers — без хардкода на фронте.
 */

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  CheckCircle2,
  ClipboardCopy,
  Download,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

import {
  archiveReportTemplate,
  cloneReportTemplate,
  createReport,
  createReportTemplate,
  deleteReportTemplate,
  downloadReportResult,
  downloadReportSampleFile,
  fetchReportCategories,
  fetchReportMeta,
  fetchReportTemplatePublishLog,
  generateReport,
  getReportTemplate,
  listReports,
  listReportTemplates,
  publishReportTemplate,
  restoreReportTemplate,
  saveBlobAsFile,
  updateReportTemplate,
  uploadReportTemplateFile,
  type DataSourceMeta,
  type ReportCategory,
  type ReportInstance,
  type ReportMeta,
  type ReportStatus,
  type ReportTemplate,
  type ReportTemplateParam,
  type ReportTemplateStatus,
} from "@/services/reports-api.service";

const STATUS_BADGE: Record<
  ReportTemplateStatus | ReportStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  draft: { label: "Черновик", variant: "secondary" },
  published: { label: "Опубликован", variant: "default" },
  archived: { label: "Архив", variant: "outline" },
  pending: { label: "Ожидает", variant: "secondary" },
  processing: { label: "Генерация…", variant: "secondary" },
  ready: { label: "Готов", variant: "default" },
  failed: { label: "Ошибка", variant: "destructive" },
};

const ENGINE_LABEL: Record<string, string> = {
  docxtpl: "docx (Jinja2)",
  openpyxl: "xlsx",
};

function formatDate(d?: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleString("ru-RU");
}

function formatBytes(size?: number | null): string {
  if (size === null || size === undefined) return "—";
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

const CATEGORY_NONE = "__none__";

interface SourceRow {
  code: string;
  as: string;
  limit: string;
  organizations: string;
  types: string;
}

interface TemplateForm {
  code: string;
  name: string;
  description: string;
  engine: string;
  category_id: string;
  organization_id: string;
  is_active: boolean;
  is_default: boolean;
  context_meta: string;
  sources: SourceRow[];
  showAdvancedJson: boolean;
  params: ReportTemplateParam[];
}

function emptySourceRow(code = "objects"): SourceRow {
  return { code, as: code, limit: "50", organizations: "", types: "" };
}

function parseFilterField(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.map(String).join(", ");
  return String(value);
}

function parseSourcesFromMeta(meta: Record<string, unknown> | null | undefined): SourceRow[] {
  const sources = Array.isArray(meta?.sources) ? meta!.sources : [];
  if (sources.length === 0) return [];
  return sources.map((raw) => {
    const s = (raw ?? {}) as Record<string, unknown>;
    const config = (s.config ?? {}) as Record<string, unknown>;
    const code = String(s.code ?? "objects");
    return {
      code,
      as: String(s.as ?? code),
      limit: config.limit !== undefined && config.limit !== null ? String(config.limit) : "",
      organizations: parseFilterField(config.organizations),
      types: parseFilterField(config.types),
    };
  });
}

function parseIdOrPlaceholderList(raw: string): unknown {
  const t = raw.trim();
  if (!t) return undefined;
  if (t.includes("{{")) return t;
  const parts = t.split(",").map((x) => x.trim()).filter(Boolean);
  const nums = parts.map((p) => Number(p));
  if (nums.every((n) => Number.isFinite(n))) {
    return nums.length === 1 ? nums[0] : nums;
  }
  return parts.length === 1 ? parts[0] : parts;
}

function buildContextMetaFromForm(form: TemplateForm): Record<string, unknown> {
  let rest: Record<string, unknown> = {};
  try {
    const parsed = form.context_meta.trim() ? JSON.parse(form.context_meta) : {};
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const { sources: _drop, ...other } = parsed as Record<string, unknown>;
      rest = other;
    }
  } catch {
    rest = {};
  }
  const sources = form.sources
    .filter((s) => s.code.trim())
    .map((s) => {
      const config: Record<string, unknown> = {};
      if (s.limit.trim()) {
        const n = Number(s.limit);
        config.limit = Number.isFinite(n) ? n : s.limit.trim();
      }
      const orgs = parseIdOrPlaceholderList(s.organizations);
      if (orgs !== undefined) config.organizations = orgs;
      const types = parseIdOrPlaceholderList(s.types);
      if (types !== undefined) config.types = types;
      return {
        code: s.code.trim(),
        as: s.as.trim() || s.code.trim(),
        config,
      };
    });
  return { ...rest, sources };
}

function syncContextMetaString(form: TemplateForm): TemplateForm {
  return {
    ...form,
    context_meta: JSON.stringify(buildContextMetaFromForm(form), null, 2),
  };
}

function applyObjectsPreset(form: TemplateForm): TemplateForm {
  const next: TemplateForm = {
    ...form,
    code: form.code.trim() || `report_objects_${Date.now().toString(36)}`,
    name: form.name.trim() || "Список объектов",
    engine: "docxtpl",
    sources: [emptySourceRow("objects")],
    params:
      form.params.length > 0
        ? form.params
        : [
            {
              code: "title",
              label: "Заголовок",
              param_type: "string",
              is_required: false,
              default_value: "Список объектов",
              display_order: 0,
            },
          ],
    showAdvancedJson: false,
  };
  return syncContextMetaString(next);
}

function emptyForm(engine = "docxtpl"): TemplateForm {
  return {
    code: "",
    name: "",
    description: "",
    engine,
    category_id: "",
    organization_id: "",
    is_active: true,
    is_default: false,
    context_meta: JSON.stringify({ sources: [] }, null, 2),
    sources: [],
    showAdvancedJson: false,
    params: [],
  };
}

function templateToForm(tpl: ReportTemplate): TemplateForm {
  let contextJson = "{}";
  try {
    contextJson = JSON.stringify(tpl.context_meta ?? {}, null, 2);
  } catch {
    contextJson = "{}";
  }
  const meta =
    tpl.context_meta && typeof tpl.context_meta === "object"
      ? (tpl.context_meta as Record<string, unknown>)
      : {};
  return {
    code: tpl.code,
    name: tpl.name,
    description: tpl.description ?? "",
    engine: tpl.engine,
    category_id: tpl.category_id ? String(tpl.category_id) : "",
    organization_id: tpl.organization_id ? String(tpl.organization_id) : "",
    is_active: tpl.is_active,
    is_default: tpl.is_default,
    context_meta: contextJson,
    sources: parseSourcesFromMeta(meta),
    showAdvancedJson: false,
    params: [...(tpl.params ?? [])],
  };
}

export default function ReportsManager() {
  const [activeTab, setActiveTab] = useState<"templates" | "reports">("templates");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5" />
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Конструктор отчётов</h2>
          <p className="text-sm text-muted-foreground">
            Шаблоны документов (docx/xlsx), декларация источников данных и генерация
            экземпляров отчётов. Справочники типов/источников — из KLS.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "templates" | "reports")}>
        <TabsList>
          <TabsTrigger value="templates" className="gap-1.5">
            <FileSpreadsheet className="h-4 w-4" />
            Шаблоны
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5">
            <FolderOpen className="h-4 w-4" />
            Отчёты
          </TabsTrigger>
        </TabsList>
        <TabsContent value="templates" className="mt-4 space-y-4 focus-visible:outline-none">
          <TemplatesTab />
        </TabsContent>
        <TabsContent value="reports" className="mt-4 space-y-4 focus-visible:outline-none">
          <ReportsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Шаблоны ─────────────────────────────────────────────────────────────
function TemplatesTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [engineFilter, setEngineFilter] = useState<string>("__all__");
  const [search, setSearch] = useState("");

  const templatesQuery = useQuery({
    queryKey: ["reports", "templates", statusFilter, engineFilter, search],
    queryFn: () =>
      listReportTemplates({
        status: statusFilter === "__all__" ? undefined : (statusFilter as ReportTemplateStatus),
        engine: engineFilter === "__all__" ? undefined : engineFilter,
        search: search || undefined,
        limit: 200,
      }),
  });

  // Редактор
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // Создание
  const [createOpen, setCreateOpen] = useState(false);
  // Клонирование
  const [cloneTarget, setCloneTarget] = useState<ReportTemplate | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  // Восстановление с конфликтом default
  const [restorePending, setRestorePending] = useState<string | null>(null);
  const [restoreMode, setRestoreMode] = useState<"replace_default" | "restore_as_non_default">(
    "replace_default",
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["reports", "templates"] });
  };

  // ──  Мутации жизненного цикла ──
  const publishMutation = useMutation({
    mutationFn: (id: string) => publishReportTemplate(id),
    onSuccess: () => {
      toast({ title: "Шаблон опубликован" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Не удалось опубликовать", description: e.message }),
  });
  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveReportTemplate(id),
    onSuccess: () => {
      toast({ title: "Шаблон архивирован" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Не удалось архивировать", description: e.message }),
  });
  const restoreMutation = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: "replace_default" | "restore_as_non_default" }) =>
      restoreReportTemplate(id, {
        replace_default: mode === "replace_default",
        restore_as_non_default: mode === "restore_as_non_default",
      }),
    onSuccess: () => {
      toast({ title: "Шаблон восстановлен" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Не удалось восстановить", description: e.message }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteReportTemplate(id),
    onSuccess: () => {
      toast({ title: "Шаблон удалён" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Не удалось удалить", description: e.message }),
  });

  const handleRestore = (tpl: ReportTemplateListItemUnion) => {
    void restoreMutation
      .mutateAsync({ id: tpl.id, mode: "replace_default" })
      .catch((e: Error) => {
        if (e.message.toLowerCase().includes("конфликтует")) {
          setRestorePending(tpl.id);
          setRestoreMode("replace_default");
        }
      });
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все статусы</SelectItem>
            <SelectItem value="draft">Черновик</SelectItem>
            <SelectItem value="published">Опубликован</SelectItem>
            <SelectItem value="archived">Архив</SelectItem>
          </SelectContent>
        </Select>
        <Select value={engineFilter} onValueChange={setEngineFilter}>
          <SelectTrigger className="w-[160px] h-9">
            <SelectValue placeholder="Все движки" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все движки</SelectItem>
            <SelectItem value="docxtpl">docx</SelectItem>
            <SelectItem value="openpyxl">xlsx</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8 h-9 w-64"
            placeholder="Поиск по названию/коду"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button className="ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Создать шаблон
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Код</TableHead>
            <TableHead>Название</TableHead>
            <TableHead>Движок</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Default</TableHead>
            <TableHead>Версия</TableHead>
            <TableHead className="text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(templatesQuery.data?.items ?? []).map((tpl) => (
            <TableRow key={tpl.id}>
              <TableCell className="font-mono text-xs">{tpl.code}</TableCell>
              <TableCell className="max-w-64 truncate">{tpl.name}</TableCell>
              <TableCell className="text-xs">
                {ENGINE_LABEL[tpl.engine] ?? tpl.engine}
              </TableCell>
              <TableCell>
                <StatusBadge status={tpl.status} />
              </TableCell>
              <TableCell>{tpl.is_default ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : "—"}</TableCell>
              <TableCell className="text-xs">v{tpl.version}</TableCell>
              <TableCell className="text-right">
                <div className="inline-flex flex-wrap items-center justify-end gap-1">
                  <LifecycleActions
                    status={tpl.status}
                    onEdit={() => {
                      setEditingId(tpl.id);
                      setEditorOpen(true);
                    }}
                    onPublish={() => publishMutation.mutate(tpl.id)}
                    onArchive={() => archiveMutation.mutate(tpl.id)}
                    onRestore={() => handleRestore(tpl)}
                    onClone={() => {
                      getReportTemplate(tpl.id)
                        .then((full) => {
                          setCloneTarget(full);
                          setCloneOpen(true);
                        })
                        .catch(() => undefined);
                    }}
                    onDelete={() => deleteMutation.mutate(tpl.id)}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {!templatesQuery.isLoading && (templatesQuery.data?.items ?? []).length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                Шаблонов нет. Создайте первый черновик.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {editorOpen && (
        <TemplateEditor
          templateId={editingId}
          onClose={() => setEditorOpen(false)}
          onSaved={() => invalidate()}
        />
      )}
      {createOpen && (
        <TemplateCreateDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
      {cloneOpen && cloneTarget && (
        <CloneDialog
          source={cloneTarget}
          onClose={() => setCloneOpen(false)}
          onDone={() => {
            setCloneOpen(false);
            invalidate();
          }}
        />
      )}
      {restorePending && (
        <Dialog open onOpenChange={() => setRestorePending(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Конфликт «один default»</DialogTitle>
              <DialogDescription>
                Восстановление шаблона конфликтует с уже опубликованным шаблоном по
                умолчанию. Выберите режим восстановления.
              </DialogDescription>
            </DialogHeader>
            <RadioGroup
              value={restoreMode}
              onValueChange={(v) => setRestoreMode(v as "replace_default" | "restore_as_non_default")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="replace_default" id="rm-replace" />
                <Label htmlFor="rm-replace" className="font-normal">
                  Сделать этот шаблон default (снять у другого)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="restore_as_non_default" id="rm-nondefault" />
                <Label htmlFor="rm-nondefault" className="font-normal">
                  Восстановить без default
                </Label>
              </div>
            </RadioGroup>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRestorePending(null)}>
                Отмена
              </Button>
              <Button
                onClick={() => {
                  restoreMutation.mutate({ id: restorePending, mode: restoreMode });
                  setRestorePending(null);
                }}
              >
                <RotateCcw className="h-4 w-4" />
                Восстановить
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

interface ReportTemplateListItemUnion {
  id: string;
  status: ReportTemplateStatus;
}

function StatusBadge({ status }: { status: ReportTemplateStatus | ReportStatus }) {
  const cfg = STATUS_BADGE[status];
  return (
    <Badge variant={cfg?.variant ?? "secondary"} className="gap-1">
      {status === "published" || status === "ready" ? (
        <CheckCircle2 className="h-3 w-3" />
      ) : status === "failed" ? (
        <XCircle className="h-3 w-3" />
      ) : (
        <RefreshCw className="h-3 w-3" />
      )}
      {cfg?.label ?? status}
    </Badge>
  );
}

function LifecycleActions({
  status,
  onEdit,
  onPublish,
  onArchive,
  onRestore,
  onClone,
  onDelete,
}: {
  status: ReportTemplateStatus;
  onEdit: () => void;
  onPublish: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onClone: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="inline-flex flex-wrap items-center justify-end gap-1">
      <Button size="sm" variant="ghost" onClick={onEdit} title="Открыть/редактировать">
        <Pencil className="h-4 w-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={onClone} title="Клонировать">
        <ClipboardCopy className="h-4 w-4" />
      </Button>
      {status === "draft" && (
        <Button size="sm" variant="ghost" onClick={onPublish} title="Опубликовать">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        </Button>
      )}
      {status === "published" && (
        <Button size="sm" variant="ghost" onClick={onArchive} title="Архивировать">
          <Archive className="h-4 w-4 text-amber-500" />
        </Button>
      )}
      {status === "archived" && (
        <Button size="sm" variant="ghost" onClick={onRestore} title="Восстановить">
          <RotateCcw className="h-4 w-4 text-sky-500" />
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        className="text-destructive"
        onClick={onDelete}
        title="Удалить"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function useCategories() {
  return useQuery<ReportCategory[]>({
    queryKey: ["reports", "categories"],
    queryFn: fetchReportCategories,
  });
}

// ── Создание шаблона ────────────────────────────────────────────────────
function TemplateCreateDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const categoriesQuery = useCategories();
  const metaQuery = useQuery<ReportMeta>({
    queryKey: ["reports", "meta"],
    queryFn: fetchReportMeta,
  });
  const engines = metaQuery.data?.engines ?? [];
  const [form, setForm] = useState<TemplateForm>(() => emptyForm());

  const createMutation = useMutation({
    mutationFn: () => {
      let context_meta: Record<string, unknown>;
      if (form.showAdvancedJson) {
        try {
          context_meta = form.context_meta.trim() ? JSON.parse(form.context_meta) : {};
        } catch {
          throw new Error("context_meta: невалидный JSON");
        }
      } else {
        context_meta = buildContextMetaFromForm(form);
      }
      return createReportTemplate({
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description || undefined,
        engine: form.engine,
        category_id: form.category_id ? Number(form.category_id) : null,
        organization_id: form.organization_id ? Number(form.organization_id) : null,
        is_active: form.is_active,
        is_default: form.is_default,
        context_meta,
        params: form.params.map((p) => ({
          code: p.code,
          label: p.label,
          param_type: p.param_type,
          is_required: p.is_required,
          default_value: p.default_value,
          ui_hint: p.ui_hint,
          display_order: p.display_order,
        })),
      });
    },
    onSuccess: () => {
      toast({ title: "Черновик шаблона создан" });
      void queryClient.invalidateQueries({ queryKey: ["reports", "meta"] });
      onCreated();
    },
    onError: (e: Error) => toast({ title: "Ошибка создания", description: e.message }),
  });

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Новый шаблон отчёта</DialogTitle>
          <DialogDescription>
            Создаётся черновик (draft). Публикация — отдельной операцией. Можно начать с пресета
            «Список объектов».
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setForm(applyObjectsPreset(form))}>
              Пресет: Список объектов
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                downloadReportSampleFile({ engine: "docxtpl", preset: "objects" })
                  .then((blob) => saveBlobAsFile(blob, "sample_objects.docx"))
                  .catch((e: Error) =>
                    toast({ title: "Не удалось скачать образец", description: e.message }),
                  );
              }}
            >
              <Download className="h-4 w-4" />
              Образец .docx
            </Button>
          </div>
          <FormField label="Код">
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="report_objects_detailed" />
          </FormField>
          <FormField label="Название">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Подробный отчёт по объектам" />
          </FormField>
          <FormField label="Описание">
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          <FormField label="Движок">
            <Select value={form.engine} onValueChange={(v) => setForm({ ...form, engine: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {engines.length === 0 && <SelectItem value="docxtpl">docx (Jinja2)</SelectItem>}
                {engines.map((e) => (
                  <SelectItem key={e.code} value={e.code}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Категория">
            <Select
              value={form.category_id}
              onValueChange={(v) => setForm({ ...form, category_id: v === CATEGORY_NONE ? "" : v })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Без категории" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CATEGORY_NONE}>Без категории</SelectItem>
                {(categoriesQuery.data ?? []).map((c: ReportCategory) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Организация KLS (ID, пусто = все)">
            <Input
              type="number"
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
              placeholder="Например 507"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v === true })}
              />
              Активен
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.is_default}
                onCheckedChange={(v) => setForm({ ...form, is_default: v === true })}
              />
              По умолчанию
            </label>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Параметры шаблона</Label>
            <p className="text-xs text-muted-foreground">
              Поля формы при генерации отчёта (например заголовок), не колонки из БД.
            </p>
            <ParamsEditor
              params={form.params}
              onChange={(params) => setForm({ ...form, params })}
              paramTypeOptions={metaQuery.data?.param_types ?? []}
            />
          </div>
          <SourcesEditor
            sources={form.sources}
            dataSources={metaQuery.data?.data_sources ?? []}
            disabled={false}
            onChange={(sources) => setForm(syncContextMetaString({ ...form, sources }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.showAdvancedJson}
              onCheckedChange={(v) =>
                setForm(
                  v === true
                    ? syncContextMetaString({ ...form, showAdvancedJson: true })
                    : { ...form, showAdvancedJson: false },
                )
              }
            />
            Расширенный JSON (context_meta)
          </label>
          {form.showAdvancedJson && (
            <FormField label="context_meta (JSON)">
              <Textarea
                rows={6}
                className="font-mono text-xs"
                value={form.context_meta}
                onChange={(e) => setForm({ ...form, context_meta: e.target.value })}
              />
            </FormField>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={!form.code.trim() || !form.name.trim()}>
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourcesEditor({
  sources,
  dataSources,
  disabled,
  onChange,
}: {
  sources: SourceRow[];
  dataSources: DataSourceMeta[];
  disabled?: boolean;
  onChange: (sources: SourceRow[]) => void;
}) {
  const options =
    dataSources.length > 0
      ? dataSources
      : [
          { code: "objects", label: "Объекты", kind: "objects" },
          { code: "geometries", label: "Геометрии", kind: "geometries" },
          { code: "kls", label: "KLS", kind: "kls" },
        ];

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Источники данных</Label>
      <p className="text-xs text-muted-foreground">
        Какие таблицы попадут в документ. В Word используйте имя из поля «as» (например{" "}
        <code className="text-[11px]">{"{{ objects }}"}</code>).
      </p>
      {sources.map((row, idx) => (
        <div key={idx} className="rounded-md border p-2 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Источник">
              <Select
                value={row.code}
                disabled={disabled}
                onValueChange={(v) => {
                  const next = [...sources];
                  next[idx] = { ...row, code: v, as: row.as || v };
                  onChange(next);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {options.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      {o.label} ({o.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="as (имя в Word)">
              <Input
                value={row.as}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...sources];
                  next[idx] = { ...row, as: e.target.value };
                  onChange(next);
                }}
              />
            </FormField>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Лимит">
              <Input
                value={row.limit}
                disabled={disabled}
                onChange={(e) => {
                  const next = [...sources];
                  next[idx] = { ...row, limit: e.target.value };
                  onChange(next);
                }}
              />
            </FormField>
            <FormField label="Орг. ID / {{ param }}">
              <Input
                value={row.organizations}
                disabled={disabled}
                placeholder="507 или {{ org_id }}"
                onChange={(e) => {
                  const next = [...sources];
                  next[idx] = { ...row, organizations: e.target.value };
                  onChange(next);
                }}
              />
            </FormField>
            <FormField label="Типы ID / {{ param }}">
              <Input
                value={row.types}
                disabled={disabled}
                placeholder="354,355"
                onChange={(e) => {
                  const next = [...sources];
                  next[idx] = { ...row, types: e.target.value };
                  onChange(next);
                }}
              />
            </FormField>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive"
            disabled={disabled}
            onClick={() => onChange(sources.filter((_, i) => i !== idx))}
          >
            <Trash2 className="h-4 w-4" />
            Удалить источник
          </Button>
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => onChange([...sources, emptySourceRow(options[0]?.code ?? "objects")])}
      >
        <Plus className="h-4 w-4" />
        Добавить источник
      </Button>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ── Редактор шаблона ────────────────────────────────────────────────────
function TemplateEditor({
  templateId,
  onClose,
  onSaved,
}: {
  templateId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const categoriesQuery = useCategories();
  const metaQuery = useQuery<ReportMeta>({
    queryKey: ["reports", "meta"],
    queryFn: fetchReportMeta,
  });

  const templateQuery = useQuery({
    queryKey: ["reports", "template", templateId],
    queryFn: () => (templateId ? getReportTemplate(templateId) : Promise.reject(new Error("no id"))),
    enabled: !!templateId,
  });

  const [form, setForm] = useState<TemplateForm>(emptyForm());
  const [loaded, setLoaded] = useState(false);
  const [paramTypes, setParamTypes] = useState<Array<{ code: string; label: string }>>([]);
  const [publishLog, setPublishLog] = useState<Array<{ to_status: string; changed_at: string }>>([]);
  const fileInputRef = useFileInputRef();

  const tpl = templateQuery.data;

  useEffect(() => {
    if (tpl && !loaded) {
      setForm(templateToForm(tpl));
      setLoaded(true);
    }
  }, [tpl, loaded]);

  useEffect(() => {
    if (!metaQuery.data) return;
    setParamTypes(metaQuery.data.param_types ?? []);
  }, [metaQuery.data]);

  useEffect(() => {
    if (!templateId) return;
    fetchReportTemplatePublishLog(templateId)
      .then((rows) =>
        setPublishLog(rows.map((r) => ({ to_status: r.to_status, changed_at: r.changed_at }))),
      )
      .catch(() => setPublishLog([]));
  }, [templateId]);

  const isPublished = tpl?.status === "published";
  const structuredLocked = isPublished;

  const saveMutation = useMutation({
    mutationFn: () => {
      const patch: Parameters<typeof updateReportTemplate>[1] = {
        name: form.name.trim() || undefined,
        description: form.description || undefined,
        engine: form.engine,
        category_id: form.category_id ? Number(form.category_id) : null,
        organization_id: form.organization_id ? Number(form.organization_id) : null,
        is_active: form.is_active,
        is_default: form.is_default,
      };
      if (!structuredLocked) {
        if (form.showAdvancedJson) {
          try {
            patch.context_meta = form.context_meta.trim() ? JSON.parse(form.context_meta) : {};
          } catch {
            throw new Error("context_meta: невалидный JSON");
          }
        } else {
          patch.context_meta = buildContextMetaFromForm(form);
        }
        patch.params = form.params.map((p) => ({
          code: p.code,
          label: p.label,
          param_type: p.param_type,
          is_required: p.is_required,
          default_value: p.default_value,
          ui_hint: p.ui_hint,
          display_order: p.display_order,
        }));
      }
      return updateReportTemplate(templateId!, patch);
    },
    onSuccess: () => {
      toast({ title: "Шаблон сохранён" });
      void queryClient.invalidateQueries({ queryKey: ["reports", "template", templateId] });
      onSaved();
    },
    onError: (e: Error) => toast({ title: "Ошибка сохранения", description: e.message }),
  });

  const fileMutation = useMutation({
    mutationFn: (file: File) => uploadReportTemplateFile(templateId!, file),
    onSuccess: () => {
      toast({ title: "Файл шаблона загружен" });
      void queryClient.invalidateQueries({ queryKey: ["reports", "template", templateId] });
    },
    onError: (e: Error) => toast({ title: "Ошибка загрузки файла", description: e.message }),
  });

  if (!templateId) return null;
  if (templateQuery.isLoading || !templateQuery.data) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Загрузка шаблона…</DialogTitle>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const current = templateQuery.data;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {current.code}{" "}
            <span className="text-sm font-normal text-muted-foreground">· v{current.version}</span>
          </DialogTitle>
          <DialogDescription>
            <StatusBadge status={current.status} />
            {current.template_file_meta?.name && (
              <span className="ml-2 text-xs">
                файл: {current.template_file_meta.name} ({formatBytes(current.template_file_meta.size)})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="Код">
            <Input value={form.code} disabled />
          </FormField>
          <FormField label="Название">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField label="Описание">
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Движок">
              <Select
                value={form.engine}
                onValueChange={(v) => setForm({ ...form, engine: v })}
                disabled={structuredLocked}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="docxtpl">docx (Jinja2)</SelectItem>
                  <SelectItem value="openpyxl">xlsx</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Категория">
              <Select
                value={form.category_id}
                onValueChange={(v) => setForm({ ...form, category_id: v === CATEGORY_NONE ? "" : v })}
                disabled={structuredLocked}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Без категории" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CATEGORY_NONE}>Без категории</SelectItem>
                  {(categoriesQuery.data ?? []).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Организация KLS (ID, пусто = все)">
              <Input
                type="number"
                value={form.organization_id}
                onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                disabled={structuredLocked}
              />
            </FormField>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v === true })}
                />
                Активен
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.is_default}
                  onCheckedChange={(v) => setForm({ ...form, is_default: v === true })}
                />
                По умолчанию
              </label>
            </div>
          </div>

          {structuredLocked && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Шаблон опубликован: структурные атрибуты (параметры, источники, файл) недоступны.
              Редактируются только имя, описание и флаги. Чтобы изменить структуру — клонируйте.
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Параметры шаблона</Label>
            <p className="text-xs text-muted-foreground">
              Поля формы при генерации отчёта (например заголовок), не колонки из БД.
            </p>
            <ParamsEditor
              params={form.params}
              onChange={(params) => setForm({ ...form, params })}
              paramTypeOptions={paramTypes}
              disabled={structuredLocked}
            />
          </div>

          <SourcesEditor
            sources={form.sources}
            dataSources={metaQuery.data?.data_sources ?? []}
            disabled={structuredLocked}
            onChange={(sources) => setForm(syncContextMetaString({ ...form, sources }))}
          />

          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.showAdvancedJson}
              disabled={structuredLocked}
              onCheckedChange={(v) =>
                setForm(
                  v === true
                    ? syncContextMetaString({ ...form, showAdvancedJson: true })
                    : { ...form, showAdvancedJson: false },
                )
              }
            />
            Расширенный JSON (context_meta)
          </label>
          {form.showAdvancedJson && (
            <FormField label="context_meta (JSON)">
              <Textarea
                rows={6}
                className="font-mono text-xs"
                value={form.context_meta}
                onChange={(e) => setForm({ ...form, context_meta: e.target.value })}
                disabled={structuredLocked}
              />
            </FormField>
          )}

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Файл шаблона</Label>
            <p className="text-xs text-muted-foreground">
              Загрузите .docx с плейсхолдерами Jinja2 или скачайте образец под источник objects.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept={form.engine === "openpyxl" ? ".xlsx" : ".docx"}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) fileMutation.mutate(file);
                  e.currentTarget.value = "";
                }}
              />
              <Button
                variant="outline"
                disabled={structuredLocked}
                onClick={() => fileInputRef.current?.click()}
              >
                Загрузить файл
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={structuredLocked || form.engine !== "docxtpl"}
                onClick={() => {
                  downloadReportSampleFile({ engine: "docxtpl", preset: "objects" })
                    .then((blob) => saveBlobAsFile(blob, "sample_objects.docx"))
                    .catch((e: Error) =>
                      toast({ title: "Не удалось скачать образец", description: e.message }),
                    );
                }}
              >
                <Download className="h-4 w-4" />
                Образец .docx
              </Button>
              {current.template_file_meta?.name && (
                <span className="text-xs text-muted-foreground">
                  {current.template_file_meta.name} · {formatBytes(current.template_file_meta.size)}
                </span>
              )}
            </div>
            {structuredLocked && (
              <p className="text-xs text-muted-foreground">Файл опубликованного шаблона не меняется.</p>
            )}
          </div>

          {publishLog.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Журнал статусов</Label>
              <div className="space-y-1">
                {publishLog.map((row, i) => (
                  <div key={i} className="text-xs text-muted-foreground">
                    {formatDate(row.changed_at)} → <Badge variant="outline">{row.to_status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useFileInputRef(): RefObject<HTMLInputElement | null> {
  return useRef<HTMLInputElement | null>(null);
}

// ── Редактор параметров ─────────────────────────────────────────────────
function ParamsEditor({
  params,
  onChange,
  paramTypeOptions = [],
  disabled = false,
}: {
  params: ReportTemplateParam[];
  onChange: (params: ReportTemplateParam[]) => void;
  paramTypeOptions?: Array<{ code: string; label: string }>;
  disabled?: boolean;
}) {
  const add = () => {
    const firstType = paramTypeOptions[0]?.code ?? "string";
    onChange([
      ...params,
      {
        code: "",
        label: "",
        param_type: firstType,
        is_required: false,
        default_value: null,
        display_order: (params.length + 1) * 100,
      },
    ]);
  };
  const update = (i: number, patch: Partial<ReportTemplateParam>) => {
    onChange(params.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  };
  const remove = (i: number) => onChange(params.filter((_, j) => j !== i));

  return (
    <div className="space-y-2">
      {(params ?? []).length === 0 && (
        <p className="text-xs text-muted-foreground">Параметры не заданы.</p>
      )}
      {params.map((p, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <Input
            className="col-span-3"
            placeholder="code"
            value={p.code}
            disabled={disabled}
            onChange={(e) => update(i, { code: e.target.value })}
          />
          <Input
            className="col-span-3"
            placeholder="Метка"
            value={p.label}
            disabled={disabled}
            onChange={(e) => update(i, { label: e.target.value })}
          />
          <Select
            value={p.param_type}
            onValueChange={(v) => update(i, { param_type: v })}
            disabled={disabled}
          >
            <SelectTrigger className="col-span-3 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {paramTypeOptions.length === 0 && <SelectItem value="string">string</SelectItem>}
              {paramTypeOptions.map((t) => (
                <SelectItem key={t.code} value={t.code}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="col-span-2 flex items-center gap-2 text-xs" title="Обязательный">
            <Checkbox
              checked={p.is_required}
              disabled={disabled}
              onCheckedChange={(v) => update(i, { is_required: v === true })}
            />
            обяз.
          </label>
          {!disabled && (
            <Button size="sm" variant="ghost" onClick={() => remove(i)} className="col-span-1">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="h-4 w-4" />
          Добавить параметр
        </Button>
      )}
    </div>
  );
}

// ── Клонирование ────────────────────────────────────────────────────────
function CloneDialog({
  source,
  onClose,
  onDone,
}: {
  source: ReportTemplate;
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const [code, setCode] = useState(source.code ? `${source.code}_clone` : "");
  const [name, setName] = useState(`${source.name} (копия)`);

  const cloneMutation = useMutation({
    mutationFn: () => cloneReportTemplate(source.id, { code: code.trim(), name: name.trim() || undefined }),
    onSuccess: () => {
      toast({ title: "Шаблон клонирован в черновик" });
      onDone();
    },
    onError: (e: Error) => toast({ title: "Ошибка клонирования", description: e.message }),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Клонировать шаблон</DialogTitle>
          <DialogDescription>
            {source.code} · v{source.version} → новый черновик
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Новый код">
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </FormField>
          <FormField label="Название">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => cloneMutation.mutate()} disabled={!code.trim()}>
            Клонировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Отчёты (экземпляры) ─────────────────────────────────────────────────
function ReportsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [createOpen, setCreateOpen] = useState(false);

  const reportsQuery = useQuery({
    queryKey: ["reports", "instances", statusFilter],
    queryFn: () =>
      listReports({
        status: statusFilter === "__all__" ? undefined : (statusFilter as ReportStatus),
        limit: 200,
      }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["reports", "instances"] });
  };

  const generateMutation = useMutation({
    mutationFn: (id: string) => generateReport(id),
    onSuccess: () => {
      toast({ title: "Генерация запущена" });
      setTimeout(invalidate, 1500);
    },
    onError: (e: Error) => {
      const msg = e.message || "";
      if (msg.toLowerCase().includes("уже готов")) {
        toast({
          title: "Отчёт уже готов",
          description: "Используйте кнопку «Скачать». Повторная генерация не нужна.",
        });
        return;
      }
      toast({ title: "Ошибка генерации", description: msg });
    },
  });

  const download = (r: ReportInstance) => {
    if (r.status !== "ready") {
      toast({ title: "Отчёт ещё не готов", description: `Статус: ${r.status}` });
      return;
    }
    downloadReportResult(r.id)
      .then((blob) => saveBlobAsFile(blob, r.file_name || `report_${r.id.slice(0, 8)}.docx`))
      .catch((e: Error) => toast({ title: "Ошибка скачивания", description: e.message }));
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все статусы</SelectItem>
            <SelectItem value="pending">Ожидает</SelectItem>
            <SelectItem value="processing">Генерация…</SelectItem>
            <SelectItem value="ready">Готов</SelectItem>
            <SelectItem value="failed">Ошибка</SelectItem>
          </SelectContent>
        </Select>
        <Button className="ml-auto" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Создать отчёт
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Статус</TableHead>
            <TableHead>Файл</TableHead>
            <TableHead>Размер</TableHead>
            <TableHead>Создан</TableHead>
            <TableHead className="text-right">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(reportsQuery.data?.items ?? []).map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{r.id.slice(0, 8)}</TableCell>
              <TableCell>
                <StatusBadge status={r.status} />
              </TableCell>
              <TableCell className="text-xs max-w-48 truncate">{r.file_name ?? "—"}</TableCell>
              <TableCell className="text-xs">{formatBytes(r.file_size)}</TableCell>
              <TableCell className="text-xs">{formatDate(r.created_at)}</TableCell>
              <TableCell className="text-right">
                <div className="inline-flex gap-1">
                  {r.status === "ready" ? (
                    <Button size="sm" onClick={() => download(r)} title="Скачать">
                      <Download className="h-4 w-4" />
                      Скачать
                    </Button>
                  ) : r.status === "processing" ? (
                    <Button size="sm" variant="ghost" disabled title="Генерация…">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant={r.status === "failed" ? "outline" : "ghost"}
                      onClick={() => generateMutation.mutate(r.id)}
                      title={r.status === "failed" ? "Повторить генерацию" : "Сгенерировать"}
                    >
                      <RefreshCw className="h-4 w-4" />
                      {r.status === "failed" ? "Повторить" : "Сгенерировать"}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {(reportsQuery.data?.items ?? []).length === 0 && !reportsQuery.isLoading && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                Отчётов нет.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {createOpen && (
        <CreateReportDialog
          onClose={() => setCreateOpen(false)}
          onDone={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
    </>
  );
}

function CreateReportDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const { toast } = useToast();
  const templatesQuery = useQuery({
    queryKey: ["reports", "templates"],
    queryFn: () => listReportTemplates({ status: "published", limit: 200 }),
  });
  const [templateId, setTemplateId] = useState("");
  const [params, setParams] = useState<Record<string, string>>({});

  const published = (templatesQuery.data?.items ?? []).filter((t) => t.status === "published");

  const selectedId = published.find((t) => t.id === templateId)?.id;

  const [paramMeta, setParamMeta] = useState<ReportTemplateParam[]>([]);

  useEffect(() => {
    if (!selectedId) return;
    getReportTemplate(selectedId)
      .then((tpl) => {
        setParamMeta(tpl.params);
        const next: Record<string, string> = {};
        for (const p of tpl.params) {
          const def = p.default_value ?? "";
          next[p.code] = typeof def === "string" ? def : def === null ? "" : JSON.stringify(def);
        }
        setParams(next);
      })
      .catch(() => {
        setParamMeta([]);
      });
  }, [selectedId]);

  const createMutation = useMutation({
    mutationFn: () =>
      createReport({
        template_id: templateId,
        params: Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, parseParamValue(v)]),
        ),
      }),
    onSuccess: () => {
      toast({ title: "Отчёт создан", description: "Запустите генерацию кнопкой в таблице" });
      onDone();
    },
    onError: (e: Error) => toast({ title: "Ошибка создания", description: e.message }),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Создать отчёт</DialogTitle>
          <DialogDescription>
            Выберите опубликованный шаблон и заполните параметры (при наличии).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Шаблон (published)">
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите шаблон" />
              </SelectTrigger>
              <SelectContent>
                {published.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.code} — {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          {paramMeta.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Параметры</Label>
              {paramMeta.map((p) => (
                <FormField key={p.code} label={`${p.label}${p.is_required ? " *" : ""}`}>
                  <Input
                    value={params[p.code] ?? ""}
                    onChange={(e) => setParams({ ...params, [p.code]: e.target.value })}
                    placeholder={p.ui_hint ?? p.code}
                  />
                </FormField>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={!templateId}>
            Создать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function parseParamValue(v: string): unknown {
  const trimmed = v.trim();
  if (trimmed === "") return "";
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;
  if (/^-?\d+$/.test(trimmed)) return Number(trimmed);
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}